import { useMemo, useState } from "react";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
} from "react-icons/fa";
import {
  FiFile,
  FiFileText,
  FiImage,
  FiMusic,
  FiFilm,
} from "react-icons/fi";
import { classifyCloudFile, isRawCloudImage } from "../utils/cloudFileTypes";
import { getCloudFileStreamUrl, getCloudFilePreviewUrl } from "../api/mintSpaceCloud";

export const getCloudFileIconColor = (kind) => {
  switch (kind) {
    case "pdf":
      return "text-status-danger";
    case "word":
      return "text-blue-600";
    case "excel":
      return "text-status-success";
    case "powerpoint":
      return "text-status-warning";
    case "mp3":
      return "text-violet-600";
    case "mp4":
      return "text-status-danger";
    case "text":
      return "text-slate-600";
    case "image":
      return "text-status-success";
    default:
      return "text-blue-600";
  }
};

export const getCloudFileIconBg = (kind, isDark = false) => {
  if (isDark) {
    switch (kind) {
      case "pdf":
        return "bg-red-950";
      case "word":
        return "bg-blue-950";
      case "excel":
        return "bg-emerald-950";
      case "powerpoint":
        return "bg-orange-950";
      case "mp3":
        return "bg-violet-950";
      case "mp4":
        return "bg-rose-950";
      case "text":
        return "bg-slate-800";
      case "image":
        return "bg-slate-800";
      default:
        return "bg-slate-800";
    }
  }
  switch (kind) {
    case "pdf":
      return "bg-status-danger-bg";
    case "word":
      return "bg-blue-100";
    case "excel":
      return "bg-status-success-bg";
    case "powerpoint":
      return "bg-status-warning-bg";
    case "mp3":
      return "bg-violet-100";
    case "mp4":
      return "bg-status-danger-bg";
    case "text":
      return "bg-slate-100";
    case "image":
      return "bg-white";
    default:
      return "bg-slate-100";
  }
};

export const CloudIconBox = ({ children, bgClass, className = "h-12 w-12", overflowHidden = false }) => (
  <div
    className={`flex shrink-0 items-center justify-center rounded-radius-xs shadow-md ring-1 ring-black/10 ${bgClass} ${className} ${
      overflowHidden ? "overflow-hidden" : ""
    }`}
  >
    {children}
  </div>
);

export const CloudFileTypeIcon = ({ kind, isDark = false, className = "h-12 w-12" }) => {
  const color = getCloudFileIconColor(kind);
  const bg = getCloudFileIconBg(kind, isDark);
  const iconClass = `h-8 w-8 drop-shadow-sm ${color}`;

  const icon = (() => {
    switch (kind) {
      case "pdf":
        return <FaFilePdf className={iconClass} />;
      case "word":
        return <FaFileWord className={iconClass} />;
      case "excel":
        return <FaFileExcel className={iconClass} />;
      case "powerpoint":
        return <FaFilePowerpoint className={iconClass} />;
      case "text":
        return <FiFileText className={iconClass} />;
      case "mp3":
        return <FiMusic className={iconClass} />;
      case "mp4":
        return <FiFilm className={iconClass} />;
      case "image":
        return <FiImage className={iconClass} />;
      default:
        return <FiFile className={iconClass} />;
    }
  })();

  return (
    <CloudIconBox bgClass={bg} className={className}>
      {icon}
    </CloudIconBox>
  );
};

const CloudFileIcon = ({ mintSpaceId, file, isDark = false, className = "h-12 w-12" }) => {
  const kind = classifyCloudFile(file);
  const [thumbFailed, setThumbFailed] = useState(false);

  const imageSrc = useMemo(() => {
    if (kind !== "image" || !mintSpaceId || !file?._id) return null;
    return isRawCloudImage(file)
      ? getCloudFilePreviewUrl(mintSpaceId, file._id)
      : getCloudFileStreamUrl(mintSpaceId, file._id);
  }, [kind, mintSpaceId, file]);

  if (kind === "image" && imageSrc && !thumbFailed) {
    return (
      <CloudIconBox
        bgClass={getCloudFileIconBg("image", isDark)}
        className={className}
        overflowHidden
      >
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setThumbFailed(true)}
        />
      </CloudIconBox>
    );
  }

  return <CloudFileTypeIcon kind={kind} isDark={isDark} className={className} />;
};

export default CloudFileIcon;
